import { useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu } from 'lucide-react';
import { Sidebar } from './Sidebar';
import { Breadcrumbs } from './Breadcrumbs';
import { LanguageToggle } from './LanguageToggle';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { DmpLookupProvider } from '@/components/dmp/DmpLookup';

// Reduced from uniticket AppLayout: same sidebar + breadcrumbs + mobile sheet, but the rich TopBar
// (search / notifications / profile) is replaced by a minimal header (page title + mobile menu).
const PAGE_TITLE_KEYS: Record<string, string> = {
  '/address-validations': 'pages.addressValidations',
  '/admin/users': 'pages.userManagement',
};

export function AppLayout() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { t } = useTranslation('common');

  let pageTitleKey = PAGE_TITLE_KEYS[location.pathname];
  if (!pageTitleKey && location.pathname.startsWith('/tickets/') && location.pathname.endsWith('/address-validation')) {
    pageTitleKey = 'pages.addressValidation';
  }
  const pageTitle = pageTitleKey ? t(pageTitleKey) : undefined;

  return (
    // DMP console (user ask 2026-07-30): mounted at layout level so any <JvidLink> anywhere opens
    // the read-only drawer over the current page, without navigation.
    <DmpLookupProvider>
    <div className="flex min-h-screen bg-slate-50">
      {/* Desktop sidebar — sticky, full viewport height so the nav scrolls internally and the user card +
          Sign Out stay pinned to the bottom-left of the screen even when the page scrolls. */}
      <div className="hidden md:flex md:flex-shrink-0 sticky top-0 h-screen self-start">
        <Sidebar />
      </div>

      {/* Mobile sidebar (Sheet overlay) */}
      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetContent side="left" className="p-0 w-64">
          <Sidebar onNavigate={() => setMobileMenuOpen(false)} />
        </SheetContent>
      </Sheet>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="flex items-center gap-3 h-16 px-4 md:px-8 bg-white border-b border-slate-200">
          <button
            type="button"
            onClick={() => setMobileMenuOpen(true)}
            className="md:hidden p-2 -ml-2 rounded-lg text-slate-500 hover:bg-slate-100"
            aria-label="Open menu"
          >
            <Menu className="w-5 h-5" />
          </button>
          {pageTitle && <h1 className="text-base font-bold text-slate-800">{pageTitle}</h1>}
          {/* Language toggle (EN | FR) — visible on every page, top-right. */}
          <div className="ml-auto flex items-center">
            <LanguageToggle />
          </div>
        </header>
        <Breadcrumbs />
        <main className="flex-1 px-4 py-6 md:px-8">
          <Outlet />
        </main>
      </div>
    </div>
    </DmpLookupProvider>
  );
}
